(() => {
  const CONTAINER_SELECTORS = [
    '.card__media > .media',
    '.article-card__image',
    '.product-media-container .media',
    '.product-media-custom-carousel__viewer',
    '.product-media-custom-carousel__thumb',
    '.product-card-swatch--image',
    '.thumbnail',
    '.wishlist-poster-card__media',
    '.image-with-text__media.media',
    '.banner__media.media',
    '.slideshow__media',
    '.collage-card .media',
    '.collapsible-content__media.media',
    '.collection-hero__image-container.media',
    '.multicolumn-card__image-wrapper .media',
    '.multirow__inner .media',
    '.video-section__poster.media',
    '.seeyoo-hero-rail__media',
    '.seeyoo-hero-rail-product__media',
  ];

  const SELECTOR = CONTAINER_SELECTORS.join(',');
  const IMAGE_SELECTOR = CONTAINER_SELECTORS.map((selector) => `${selector} img`).join(',');

  const EXCLUDE_SELECTOR = [
    '.header__heading-logo',
    '.facets__image',
    '.cart-item__image',
    '.cart-notification-product__image',
    '.cart-disclosure-indicator',
    '.icon-with-text__icon',
  ].join(',');

  const hasImageSource = (image) =>
    Boolean(image.currentSrc || image.src || image.getAttribute('src') || image.getAttribute('srcset'));

  const isTrackableImage = (image) =>
    image instanceof HTMLImageElement && hasImageSource(image) && !image.closest(EXCLUDE_SELECTOR);

  const isLoaded = (image) => image.complete && image.naturalWidth > 0;

  const syncImageState = (image) => {
    image.classList.toggle('is-media-image-loaded', isLoaded(image));
    image.classList.toggle(
      'is-media-image-error',
      image.complete && (image.dataset.mediaLoadingError === 'true' || image.naturalWidth === 0)
    );
  };

  const setContainerState = (container) => {
    const images = Array.from(container.querySelectorAll('img')).filter(isTrackableImage);
    images.forEach(syncImageState);
    const primaryImages = images.filter((image) => image.hasAttribute('data-product-card-primary-image'));
    const stateImages = primaryImages.length ? primaryImages : images.slice(0, 1);

    if (!images.length) {
      container.dataset.mediaLoadingReady = 'false';
      container.classList.remove('media-load-state', 'is-media-loaded', 'is-media-error');
      return;
    }

    container.dataset.mediaLoadingReady = 'true';
    const hasLoadedImage = stateImages.some(isLoaded);
    const hasFinished = stateImages.every((image) => image.complete);
    const hasOnlyErrors =
      !hasLoadedImage &&
      hasFinished &&
      stateImages.some((image) => image.dataset.mediaLoadingError === 'true' || image.naturalWidth === 0);

    container.classList.add('media-load-state');
    container.classList.toggle('is-media-loaded', hasLoadedImage);
    container.classList.toggle('is-media-error', hasOnlyErrors);
  };

  const updateImage = (image) => {
    syncImageState(image);

    CONTAINER_SELECTORS.forEach((selector) => {
      const container = image.closest(selector);
      if (container) setContainerState(container);
    });
  };

  const bindImage = (image) => {
    if (!isTrackableImage(image) || image.dataset.mediaLoadingBound === 'true') return;

    image.dataset.mediaLoadingBound = 'true';
    image.addEventListener('load', () => {
      image.dataset.mediaLoadingError = 'false';
      updateImage(image);
    });
    image.addEventListener('error', () => {
      image.dataset.mediaLoadingError = 'true';
      updateImage(image);
    });
    updateImage(image);
  };

  const scan = (root = document) => {
    root.querySelectorAll(IMAGE_SELECTOR).forEach(bindImage);
    root.querySelectorAll(SELECTOR).forEach(setContainerState);
  };

  const start = () => {
    scan();

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.target instanceof HTMLImageElement) {
          mutation.target.dataset.mediaLoadingError = 'false';
          mutation.target.classList.remove('is-media-image-loaded', 'is-media-image-error');
          bindImage(mutation.target);
          updateImage(mutation.target);
          return;
        }

        mutation.addedNodes.forEach((node) => {
          if (node instanceof HTMLImageElement) bindImage(node);
          if (node instanceof Element) scan(node);
        });
      });
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['src', 'srcset', 'sizes'],
      childList: true,
      subtree: true,
    });

    window.addEventListener('load', () => scan(), { once: true });
    window.setTimeout(() => scan(), 1200);
    document.addEventListener('shopify:section:load', (event) => scan(event.target));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
